/**
 * Error Bar UI
 * Displays player's error level (health) with visual feedback
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';

export class ErrorBar {
    constructor(scene, x, y) {
        this.scene = scene;
        this.x = x;
        this.y = y;

        // Dimensions
        this.width = 300;
        this.height = 20;

        // Current error level (0-100)
        this.currentError = 0;
        this.displayError = 0; // For smooth animation

        // Create container - FIXED to camera (doesn't scroll)
        this.container = scene.add.container(x, y).setDepth(100).setScrollFactor(0);

        // Create UI elements
        this.createBar();
        this.createLabel();
    }

    createBar() {
        // Background
        this.background = this.scene.add.graphics();
        this.background.fillStyle(0x333333, 0.8);
        this.background.fillRoundedRect(
            -this.width / 2, -this.height / 2,
            this.width, this.height, 5
        );
        this.container.add(this.background);

        // Fill bar
        this.fillBar = this.scene.add.graphics();
        this.container.add(this.fillBar);

        // Border
        this.border = this.scene.add.graphics();
        this.border.lineStyle(2, 0x666666, 1);
        this.border.strokeRoundedRect(
            -this.width / 2, -this.height / 2,
            this.width, this.height, 5
        );
        this.container.add(this.border);

        // Danger markers
        this.createDangerMarkers();
    }

    createDangerMarkers() {
        // Warning threshold (50%)
        const warningX = -this.width / 2 + this.width * 0.5;
        const dangerX = -this.width / 2 + this.width * 0.75;

        this.warningMarker = this.scene.add.graphics();
        this.warningMarker.lineStyle(2, CONSTANTS.COLORS.ERROR_MED, 0.6);
        this.warningMarker.moveTo(warningX, -this.height / 2 - 3);
        this.warningMarker.lineTo(warningX, this.height / 2 + 3);
        this.warningMarker.strokePath();
        this.container.add(this.warningMarker);

        // Danger threshold (75%)
        this.dangerMarker = this.scene.add.graphics();
        this.dangerMarker.lineStyle(2, CONSTANTS.COLORS.ERROR_HIGH, 0.6);
        this.dangerMarker.moveTo(dangerX, -this.height / 2 - 3);
        this.dangerMarker.lineTo(dangerX, this.height / 2 + 3);
        this.dangerMarker.strokePath();
        this.container.add(this.dangerMarker);
    }

    createLabel() {
        // "ERROR" label
        this.label = this.scene.add.text(-this.width / 2 - 60, 0, 'ERROR', {
            fontSize: '14px',
            fontFamily: 'Courier New, monospace',
            color: '#FF6666',
            fontStyle: 'bold'
        }).setOrigin(0, 0.5);
        this.container.add(this.label);

        // Percentage text
        this.percentText = this.scene.add.text(this.width / 2 + 10, 0, '0%', {
            fontSize: '14px',
            fontFamily: 'Courier New, monospace',
            color: '#FFFFFF'
        }).setOrigin(0, 0.5);
        this.container.add(this.percentText);
    }

    setError(value) {
        this.currentError = Phaser.Math.Clamp(value, 0, 100);
    }

    update() {
        // Smooth animation
        this.displayError = Phaser.Math.Linear(
            this.displayError,
            this.currentError,
            0.1
        );

        this.render();
    }

    render() {
        const fillWidth = (this.displayError / 100) * (this.width - 4);

        // Determine color based on error level
        let color;
        if (this.displayError < 50) {
            color = CONSTANTS.COLORS.ERROR_LOW;
        } else if (this.displayError < 75) {
            color = CONSTANTS.COLORS.ERROR_MED;
        } else {
            color = CONSTANTS.COLORS.ERROR_HIGH;
        }

        // Clear and redraw fill
        this.fillBar.clear();
        this.fillBar.fillStyle(color, 0.9);
        this.fillBar.fillRoundedRect(
            -this.width / 2 + 2,
            -this.height / 2 + 2,
            fillWidth,
            this.height - 4,
            3
        );

        // Glow effect at high error
        if (this.displayError > 75) {
            this.fillBar.fillStyle(color, 0.3);
            this.fillBar.fillRoundedRect(
                -this.width / 2,
                -this.height / 2 - 2,
                fillWidth + 4,
                this.height + 4,
                5
            );

            // Pulse effect
            const pulse = Math.sin(this.scene.time.now * 0.01) * 0.3 + 0.7;
            this.label.setAlpha(pulse);
        } else {
            this.label.setAlpha(1);
        }

        // Update percentage text
        this.percentText.setText(`${Math.floor(this.displayError)}%`);

        // Color the percentage based on level
        if (this.displayError > 75) {
            this.percentText.setColor('#FF0000');
        } else if (this.displayError > 50) {
            this.percentText.setColor('#FF6600');
        } else {
            this.percentText.setColor('#FFFFFF');
        }
    }

    destroy() {
        this.container.destroy();
    }
}
